import type { BoardDefinition } from '@skyrover/shared/types';

import type { BeginnerDeviceDefinition, BeginnerDeviceKind } from './types.js';

/**
 * Optional board.json extension shape.
 * Boards may later include `beginnerDevices` without changing Blockly blocks.
 * Until the shared schema is extended, the in-code registry is the source of truth
 * and this type documents the forward-compatible contract.
 */
export interface BoardWithBeginnerDevices extends BoardDefinition {
  beginnerDevices?: BeginnerDeviceDefinition[];
}

/** Default devices when a board has no custom list. */
const DEFAULT_DEVICES: BeginnerDeviceDefinition[] = [
  {
    id: 'speaker-1',
    label: 'Speaker 1',
    kind: 'speaker',
    mapping: { role: 'default-speaker' },
  },
  {
    id: 'microphone-1',
    label: 'Microphone 1',
    kind: 'microphone',
    mapping: { role: 'default-microphone' },
  },
  {
    id: 'lcd-1',
    label: 'LCD 1',
    kind: 'lcd',
    mapping: { cols: 16, rows: 2 },
  },
  {
    id: 'oled-1',
    label: 'OLED 1',
    kind: 'oled',
    mapping: { chip: 'SSD1306' },
  },
  {
    id: 'tft-1',
    label: 'TFT 1',
    kind: 'tft',
    mapping: { chip: 'ST7735' },
  },
];

const SPEAKER_MIC_SKYROVER: BeginnerDeviceDefinition[] = [
  {
    id: 'speaker-1',
    label: 'Speaker 1',
    kind: 'speaker',
    mapping: {
      chip: 'MAX98357A',
      bclk: 20,
      din: 19,
      ws: 8,
    },
  },
  {
    id: 'microphone-1',
    label: 'Microphone 1',
    kind: 'microphone',
    mapping: {
      chip: 'INMP441',
      sck: 20,
      sd: 15,
      ws: 8,
    },
  },
];

const SPEAKER_MIC_ESP32: BeginnerDeviceDefinition[] = [
  {
    id: 'speaker-1',
    label: 'Speaker 1',
    kind: 'speaker',
    mapping: {
      chip: 'MAX98357A',
      bclk: 20,
      din: 19,
      ws: 8,
    },
  },
  {
    id: 'microphone-1',
    label: 'Microphone 1',
    kind: 'microphone',
    mapping: {
      chip: 'INMP441',
      sck: 20,
      sd: 15,
      ws: 8,
    },
  },
];

/**
 * Per-board beginner device catalogs.
 * Future boards register peripherals here (or via board.beginnerDevices).
 */
const BOARD_DEVICE_REGISTRY: Readonly<Record<string, BeginnerDeviceDefinition[]>> = {
  'skyrover-esp32-s3': [
    ...SPEAKER_MIC_SKYROVER,
    {
      id: 'tft-1',
      label: 'TFT 1',
      kind: 'tft',
      mapping: { chip: 'ST7735' },
    },
  ],
  'esp32-devkit': [
    ...SPEAKER_MIC_ESP32,
    {
      id: 'oled-1',
      label: 'OLED 1',
      kind: 'oled',
      mapping: { chip: 'SSD1306' },
    },
  ],
  'esp8266-nodemcu': [
    {
      id: 'oled-1',
      label: 'OLED 1',
      kind: 'oled',
      mapping: { chip: 'SSD1306' },
    },
  ],
  'arduino-uno': [
    {
      id: 'speaker-1',
      label: 'Speaker 1',
      kind: 'speaker',
      mapping: { role: 'board-speaker-1' },
    },
    {
      id: 'microphone-1',
      label: 'Microphone 1',
      kind: 'microphone',
      mapping: { role: 'board-microphone-1' },
    },
    {
      id: 'lcd-1',
      label: 'LCD 1',
      kind: 'lcd',
      mapping: { cols: 16, rows: 2 },
    },
  ],
  'arduino-nano': [
    {
      id: 'lcd-1',
      label: 'LCD 1',
      kind: 'lcd',
      mapping: { cols: 16, rows: 2 },
    },
  ],
  'arduino-mega-2560': [
    {
      id: 'lcd-1',
      label: 'LCD 1',
      kind: 'lcd',
      mapping: { cols: 16, rows: 2 },
    },
  ],
};

function devicesFromBoardJson(board: BoardDefinition | null): BeginnerDeviceDefinition[] | null {
  if (!board) {
    return null;
  }
  const extended = board as BoardWithBeginnerDevices;
  if (Array.isArray(extended.beginnerDevices) && extended.beginnerDevices.length > 0) {
    return extended.beginnerDevices;
  }
  return null;
}

/** All beginner devices for the active / given board. */
export function getBoardBeginnerDevices(
  board: BoardDefinition | null,
): BeginnerDeviceDefinition[] {
  const fromJson = devicesFromBoardJson(board);
  if (fromJson) {
    return fromJson;
  }
  if (board?.id) {
    const registered = BOARD_DEVICE_REGISTRY[board.id];
    if (registered) {
      return registered;
    }
  }
  return DEFAULT_DEVICES;
}

export function getBoardBeginnerDevicesByKind(
  board: BoardDefinition | null,
  kind: BeginnerDeviceKind,
): BeginnerDeviceDefinition[] {
  return getBoardBeginnerDevices(board).filter((device) => device.kind === kind);
}

export function findBeginnerDevice(
  board: BoardDefinition | null,
  deviceId: string,
): BeginnerDeviceDefinition | undefined {
  return getBoardBeginnerDevices(board).find((device) => device.id === deviceId);
}

/** Boards that list at least one device of the given kind in the registry. */
export function boardHasBeginnerDeviceKind(boardId: string, kind: BeginnerDeviceKind): boolean {
  if (!boardId) {
    return true;
  }
  const registered = BOARD_DEVICE_REGISTRY[boardId];
  if (!registered) {
    return DEFAULT_DEVICES.some((device) => device.kind === kind);
  }
  return registered.some((device) => device.kind === kind);
}
